import matchAll from 'match-all';
import { ScriptureHttpError, ScriptureClient } from '@rsc/scripture-client';
import { BaseError } from 'make-error';
import { ResultAsync, err, ok, errAsync, okAsync } from 'neverthrow';
import { TranslationTree, VerseTree } from '@rsc/scripture-model';

/**
 * @file
 * Operations on strings.
 */
/**
 * Convert all hyphens and "em dashes" to "en dashes" (or the given character,
 * if specified). Also makes sure to use a character, not an html entity.
 *
 * @param str
 *   The string to convert.
 * @param dash
 *   The dash to replace with. Optional. Defaults to "en dash".
 * @returns
 *   A string with dashes replaced.
 */
function replaceDashes(str, dash = "–") {
    return str.replace(/[-–—]|&#45;|&#8211;|&#8212;|&mdash;|&ndash;/g, dash);
}
/**
 * Like parseInt, but check if the value is already a number.
 */
function ensureInt(x) {
    if (typeof x === "number") {
        return x;
    }
    return parseInt(x);
}

/**
 * @file
 * Global regular expressions.
 */
/**
 * We define words as anything that is not whitespace or an EM or EN dash.
 * Other punctuation marks are included with the words.
 *
 * NOTE: This regex exists on the back end (Drupal PHP+JS) and
 * the front end (Vue JS). Try to keep them the same.
 *
 * @type {RegExp}
 */
const wordRegex = /[^\s—–]+/gi;
/**
 * This is a regular expression for characters with strong right-to-left
 * direction. Expand as necessary.
 *
 * @type {RegExp}
 */
//                        Hebrew
const rtlRegex = /[\u0590-\u05FF]/g;
/**
 * Headings look like this [h]The Beginning[/h]
 * Or like this: [h]Praise for God's Mighty Deeds and for His Answer to
 * Prayer.[/h][h]For the choir director. A Song. A Psalm.[/h]
 */
const allHeadingsRegex = /\[h](.*)\[\/h]/;
const separateHeadingsRegex = /\[h](.*?)\[\/h]/g;

/**
 * Format a verse range from all the required bits and pieces.
 *
 * @param fromVerseLabel
 * @param toVerseLabel
 * @param bookName
 * @param fromChapterLabel
 * @param toChapterLabel
 * @param suffix
 *
 * @returns
 *   The formatted range, like Hebrews 1:1–3.
 */
function formatVerseRange(fromVerseLabel, toVerseLabel, bookName, fromChapterLabel, toChapterLabel, suffix = "") {
    // We have to use "en dashes" to represent a range of numbers when
    // presenting it on the front end, not hyphens or "em dashes".
    // See https://www.thepunctuationguide.com/en-dash.html
    fromVerseLabel = replaceDashes(fromVerseLabel).trim();
    toVerseLabel = replaceDashes(toVerseLabel).trim();
    // If a verse label contains a dash, it is a compound verse.
    // Wrap it in braces, except when it's the only verse.
    if (fromVerseLabel !== toVerseLabel) {
        if (fromVerseLabel.includes("–")) {
            fromVerseLabel = `(${fromVerseLabel})`;
        }
        if (toVerseLabel.includes("–")) {
            toVerseLabel = `(${toVerseLabel})`;
        }
    }
    // Check if the book name contains RTL characters.
    const hasRtl = !!rtlRegex.exec(bookName);
    // If the book name contains RTL characters, fix the direction of the
    // chapters and verses using an LRM character.
    const directionMark = hasRtl ? "\u200e" : "";
    bookName = bookName.trim();
    bookName = bookName.length ? `${bookName} ` : "";
    if (fromChapterLabel === toChapterLabel) {
        if (fromVerseLabel === toVerseLabel) {
            return `${directionMark}${bookName}${directionMark}${fromChapterLabel}:${fromVerseLabel}${suffix}`;
        }
        else {
            return `${directionMark}${bookName}${directionMark}${fromChapterLabel}:${fromVerseLabel}–${toVerseLabel}${suffix}`;
        }
    }
    else {
        return `${directionMark}${bookName}${directionMark}${fromChapterLabel}:${fromVerseLabel}–${toChapterLabel}:${toVerseLabel}${suffix}`;
    }
}
/**
 * Get the selected words from the array of words, inserting ellipses when skipping words.
 */
function getWordOrdinalString(words, ordinals) {
    if (!ordinals.length || !words.length) {
        return "";
    }
    let lastOrdinal = ordinals[0] ? ordinals[0] - 1 : 0;
    return ordinals
        .reduce((acc, ordinal) => {
        if (ordinal > lastOrdinal + 1) {
            // We skipped a word.
            acc.push("…");
        }
        const word = words[ordinal];
        if (word) {
            acc.push(word);
        }
        lastOrdinal = ordinal;
        return acc;
    }, [])
        .join(" ");
}
function formatBookName(book, localBook) {
    return book.name !== localBook.name
        ? `${book.name} (${localBook.name})`
        : book.name;
}
function getTranslationSuffix(enabled, name, abbr, plain) {
    if (!enabled || !name || !abbr) {
        return "";
    }
    if (plain) {
        return ` (${abbr})`;
    }
    else {
        return ` (<abbr title="${name}">${abbr}</abbr>)`;
    }
}
/**
 * Find the partial verse which contains the given word ordinal.
 *
 * @param parts
 *   The partial verses to search.
 *   This should already be sorted.
 * @param ordinal
 *   The ordinal of the word to find.
 */
function findVersePartContainingOrdinal(parts, ordinal) {
    return parts.find((part) => {
        return part.fromOrdinal <= ordinal && part.toOrdinal >= ordinal;
    });
}
/**
 * Find the verse part which contains all given word ordinals.
 *
 * @param parts
 *   The partial verses to search.
 *   This should already be sorted.
 * @param wordOrdinals
 *   The ordinals of the words to find.
 */
function findVersePartContainingAllOrdinals(parts, wordOrdinals) {
    // Find the partial verse containing the first selected word.
    const firstWordOrdinal = wordOrdinals[0];
    const firstPart = firstWordOrdinal !== undefined
        ? findVersePartContainingOrdinal(parts, firstWordOrdinal)
        : undefined;
    // Find the partial verse containing the last selected word.
    const lastWordOrdinal = wordOrdinals[wordOrdinals.length - 1];
    const lastPart = lastWordOrdinal !== undefined
        ? findVersePartContainingOrdinal(parts, lastWordOrdinal)
        : undefined;
    return firstPart === lastPart ? firstPart : undefined;
}

class ParsedReference {
    /**
     * Represents a parsed scripture reference.
     *
     * @param book
     *   The name or abbreviation of the book entered by the user.
     * @param fromChapter
     *   The first chapter in the range.
     * @param fromVerse
     *   The first verse label in the range.
     * @param toChapter
     *   The last chapter in the range.
     * @param toVerse
     *   The last verse label in the range.
     * @param singleVerse
     *   True if this range is a single verse.
     * @param singleChapter
     *   True if this range falls within a single chapter.
     */
    constructor(book, fromChapter, fromVerse, toChapter, toVerse, singleVerse, singleChapter) {
        this.book = book;
        this.fromChapter = fromChapter;
        this.fromVerse = fromVerse;
        this.toChapter = toChapter;
        this.toVerse = toVerse;
        this.singleVerse = singleVerse;
        this.singleChapter = singleChapter;
    }
    /**
     * Get a new ParsedReference, with some values replaced.
     */
    override(overrides) {
        return new ParsedReference(overrides.book !== undefined ? overrides.book : this.book, overrides.fromChapter !== undefined
            ? overrides.fromChapter
            : this.fromChapter, overrides.fromVerse !== undefined ? overrides.fromVerse : this.fromVerse, overrides.toChapter !== undefined ? overrides.toChapter : this.toChapter, overrides.toVerse !== undefined ? overrides.toVerse : this.toVerse, overrides.singleVerse !== undefined
            ? overrides.singleVerse
            : this.singleVerse, overrides.singleChapter !== undefined
            ? overrides.singleChapter
            : this.singleChapter);
    }
    /**
     * Render this parsed reference back to a string.
     */
    render() {
        const book = this.book || "";
        const fromVerse = this.fromVerse || "";
        const toVerse = this.toVerse || fromVerse;
        const fromChapter = this.fromChapter || "";
        const toChapter = this.toChapter || fromChapter;
        if (!fromChapter) {
            return book;
        }
        if (!fromVerse) {
            return `${book} ${fromChapter}`;
        }
        return formatVerseRange(fromVerse, toVerse, book, `${fromChapter}`, `${toChapter}`);
    }
}
/**
 * Parse scripture references from user input.
 *
 * @returns
 *   The parsed reference object, or null on failure.
 */
function parseReference(reference) {
    // Exclamation mark has no special meaning in RegEx.
    const placeholder = "!";
    // Reject strings containing our placeholder, so that we know we can
    // safely use it later in this function.
    if (reference.includes(placeholder)) {
        return null;
    }
    // Replace all dashes with hyphens before matching.
    // This simplifies the RegExes somewhat.
    reference = replaceDashes(reference, "-");
    // First capture and replace compound verses like (2–3), because implementing
    // them in the main regex generally makes it much to complicated.
    // regex groups:      1
    const compoundVerseRegex = /\((.*?)\)/g;
    const compoundVerseMatches = matchAll(reference, compoundVerseRegex);
    reference = reference.replace(compoundVerseRegex, "!");
    // Remote LRM chars.
    reference = reference.replace("\u200e", "");
    // Now parse the overall reference.
    // The capturing groups are used like this:
    //    1: book
    //    5: from chapter
    //    7: from verse
    //    10: to chapter
    //    12: to verse
    // regex characters:                           Latin          Hebrew       Greek
    // regex groups:        123                  4                                                        5     6         7        8           910   11         12
    const referenceRegex = /(((\d|[Ii]{1,3})\s?)?([A-Za-zÀ-ÖØ-öø-ÿ\u0590-\u05FF\u0370-\u03FF ]{2,}))[.\s]*(\d+)?(\s*:\s*)?([\w!]+)?(\s*[-]\s*)?((\d+)(\s*:\s*))?([\w!]+)?/;
    const result = referenceRegex.exec(reference);
    if (!result) {
        // The regex failed. Stop trying.
        return null;
    }
    const fromChapter = result[5] ? parseInt(result[5]) : null;
    let fromVerse = result[7] || null;
    let toChapter = result[10] ? parseInt(result[10]) : null;
    let toVerse = result[12] || null;
    // Here we can convert the placeholders back to compound verses, if needed.
    if (fromVerse === "!") {
        fromVerse = compoundVerseMatches.nextRaw()[1];
    }
    if (toVerse === "!") {
        toVerse = compoundVerseMatches.nextRaw()[1];
    }
    // Handle chapter ranges.
    if (fromVerse === null && toChapter === null && toVerse !== null) {
        // What the regex detected as a verse is actually a chapter.
        toChapter = parseInt(toVerse);
        toVerse = null;
    }
    // Check whether this is a range or not.
    let singleVerse;
    if (fromChapter && fromVerse) {
        singleVerse = toChapter === null && toVerse === null;
    }
    else {
        singleVerse = null;
    }
    // Check if the verse range resides within the boundaries of a single chapter.
    let singleChapter;
    if (fromChapter) {
        singleChapter = toChapter === null || toChapter === fromChapter;
    }
    else {
        singleChapter = null;
    }
    // Can't have a single verse spanning multiple chapters.
    if (singleChapter === false) {
        singleVerse = false;
    }
    return new ParsedReference((result[1] || "").trim(), fromChapter, fromVerse, singleChapter ? fromChapter : toChapter, singleVerse ? fromVerse : toVerse, singleVerse, singleChapter);
}

/**
 * @file
 * Operations on arrays.
 */
/**
 * Since we don't have "Array.findLast", we can use this function.
 * See https://esdiscuss.org/topic/array-findlast-and-findlastindex
 *
 * @param array
 *   The array to search through, from the end.
 * @param predicate
 *   The function that checks if the current value is the one we want.
 * @param thisArg
 *   The value of `this` when calling the predicate function.
 *
 * @returns
 *   The value we want.
 */
function findLast(array, predicate, thisArg = null) {
    return array[findLastIndex(array, predicate, thisArg)];
}
/**
 * Since we don't have "Array.findLastIndex", we can use this function.
 * See https://esdiscuss.org/topic/array-findlast-and-findlastindex
 *
 * @param array
 *   The array to search through, from the end.
 * @param predicate
 *   The function that checks if the current value is the one we want.
 * @param thisArg
 *   The value of `this` when calling the predicate function.
 *
 * @returns
 *   The index of the value we want, or -1 when not found.
 */
function findLastIndex(array, predicate, thisArg = null) {
    for (let i = array.length - 1; i >= 0; --i) {
        const x = array[i];
        if (x !== undefined && predicate.call(thisArg, x)) {
            return i;
        }
    }
    // Not found.
    return -1;
}
/**
 * Callback to filter duplicates out of array.
 *
 * Usage: `arr.filter(distinct)`
 *
 * @param value
 *   See Array.prototype.filter
 * @param index
 *   See Array.prototype.filter
 * @param arr
 *   See Array.prototype.filter
 *
 * @returns
 *   True when arr[index] is the first occurrence of `value` in `arr`.
 */
function distinct(value, index, arr) {
    return arr.indexOf(value) === index;
}
/**
 * Generate an array of integers, starting on start, and ending on end (both inclusive).
 */
function range(start, end) {
    return Array(end - start + 1)
        .fill(undefined)
        .map((_, i) => i + start);
}
/**
 * Turn an array of booleans into an array of ordinals for which the input was "true".
 * See the unit tests for examples.
 */
function booleansToOrdinals(booleans) {
    return booleans.reduce((arr, value, ordinal) => {
        if (value) {
            arr.push(ordinal);
        }
        return arr;
    }, []);
}
/**
 * Turn an array of ordinals into an array of booleans of the given length.
 * The output value is true if the ordinal of that value appears in the input array.
 * See the unit tests for examples.
 */
function ordinalsToBooleans(ordinals, length) {
    return Array(length)
        .fill(undefined)
        .map((_, i) => ordinals.includes(i));
}
/**
 * Find ranges of array indices that match the given predicate.
 *
 * This is only guaranteed to work on packed arrays with continuous integer keys, i.e. normal arrays where you didn't
 * do anything special like `arr[100]="foo"` or `arr["foo"]="bar";`.
 *
 * The returned ranges are such that you can pass them to `Array.prototype.slice()` to get the falsy elements.
 *
 * Example:
 * - input: [0, 1, 0, 0, 1, 0, 0, 0]
 * - result: [
 *   [0, 1],
 *   [2, 4],
 *   [5, 8],
 * ]
 *
 * @returns
 *   The start index (inclusive) and the end index (exclusive).
 */
function findRanges(array, predicate) {
    let start = null;
    const result = array.reduce((ranges, value, index) => {
        if (predicate(value)) {
            if (start === null) {
                // We just started a range of matching values.
                start = index;
            }
        }
        else {
            if (start !== null) {
                // We just finished a range of matching values.
                ranges.push([start, index]);
                start = null;
            }
        }
        return ranges;
    }, []);
    if (start !== null) {
        // We ended on a matching range.
        result.push([start, array.length]);
    }
    return result;
}
/**
 * Given an array of words, wrap the words in the given prefix and suffix by
 * prepending the prefix to the first word and appending the suffix to the last
 * word.
 *
 * @param words
 *   The array of words. It will be modified in place.
 * @param firstWordOrdinal
 *   The position of the first word in the array.
 * @param lastWordOrdinal
 *   The position of the last word in the array.
 * @param prefix
 *   String to prepend before the first word.
 * @param suffix
 *   String to append after the last word.
 * @param offset
 *   An amount to add to the word ordinals. May be negative. If this causes the
 *   word ordinals to refer to negative positions, those positions are clipped
 *   at 0.
 *
 * @returns
 *   The modified input array.
 */
function wrapWords(words, firstWordOrdinal, lastWordOrdinal, prefix, suffix, offset) {
    firstWordOrdinal = firstWordOrdinal + offset;
    lastWordOrdinal = lastWordOrdinal + offset;
    if (firstWordOrdinal < 0) {
        if (lastWordOrdinal < 0) {
            // Out of bounds. Nothing to wrap.
            return words;
        }
        // Beginning is out of bounds. Start at 0.
        firstWordOrdinal = 0;
    }
    if (lastWordOrdinal >= words.length) {
        if (firstWordOrdinal >= words.length) {
            // Out of bounds. Nothing to wrap.
            return words;
        }
        // End is out of bounds. End at the end of the array.
        lastWordOrdinal = words.length - 1;
    }
    words[firstWordOrdinal] = `${prefix}${words[firstWordOrdinal]}`;
    words[lastWordOrdinal] = `${words[lastWordOrdinal]}${suffix}`;
    return words;
}
/**
 * Zip two or more arrays.
 *
 * Based on https://gist.github.com/renaudtertrais/25fc5a2e64fe5d0e86894094c6989e10
 *
 * Examples:
 * const a = ;
 * const b = ;
 *
 * // Zip
 * zip([1, 2, 3], [4, 5, 6]);
 * [[1, 4], [2, 5], [3, 6]]
 *
 * // Unzip
 * zip(...[[1, 4], [2, 5], [3, 6]]);
 * [[1, 2, 3], [4, 5, 6]]
 *
 * @param array1
 *   The first array to iterate over.
 * @param arrays
 *   More arrays to iterate over.
 *
 * @returns
 *   A zipped array. The length is determined by the length of `array1`.
 */
function zip(array1, ...arrays) {
    return array1.map((val, i) => arrays.reduce((a, arr) => [...a, arr[i]], [val]));
}

class PartialVerse {
    /**
     * Represents a partial verse definition.
     *
     * @param label
     *   The label of the part, e.g. "a" or "b".
     * @param fromOrdinal
     *   The first word ordinal in this part.
     * @param toOrdinal
     *   The last word ordinal in this part.
     */
    constructor(label, fromOrdinal, toOrdinal) {
        this.label = label;
        this.fromOrdinal = fromOrdinal;
        this.toOrdinal = toOrdinal;
    }
    /**
     * Dump data in format understood by the back end.
     *
     * Inverse of `createPartialVerse`.
     *
     * @returns
     *   Partial verse data as represented by the back end.
     */
    dump() {
        return {
            label: this.label,
            fromWordOrdinal: this.fromOrdinal,
            toWordOrdinal: this.toOrdinal,
        };
    }
    /**
     * Get an array of word ordinals.
     */
    get wordOrdinals() {
        return range(this.fromOrdinal, this.toOrdinal);
    }
}
/**
 * Create a PartialVerse object from back end data.
 */
function createPartialVerse(data) {
    return new PartialVerse(data.label, data.fromWordOrdinal, data.toWordOrdinal);
}

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

const scriptureUtilErrorSymbol = Symbol("ScriptureUtilError");
const scriptureUtilReferenceRenderErrorSymbol = Symbol("ScriptureUtilReferenceRenderError");
const scriptureUtilUserInputErrorSymbol = Symbol("ScriptureUtilUserInputError");
const scriptureUtilMissingTranslationErrorSymbol = Symbol("ScriptureUtilMissingTranslationError");
const scriptureUtilMissingVerseErrorSymbol = Symbol("ScriptureUtilMissingVerseError");
const scriptureUtilInvalidVidRangeErrorSymbol = Symbol("ScriptureUtilInvalidVidRangeError");
/**
 * The root of the custom error hierarchy for scripture-util.
 *
 * Only use this for operational errors. For the distinction between operation errors and programmer errors, read:
 * https://www.joyent.com/node-js/production/design/errors
 *
 * For 'programmer errors', just use the normal Error class, and let the program crash.
 */
class ScriptureUtilError extends BaseError {
    constructor() {
        super(...arguments);
        // See https://github.com/supermacro/neverthrow/issues/451
        this.scriptureUtilError = scriptureUtilErrorSymbol;
    }
}
/**
 * Used when we fail to render a verse reference.
 */
class ScriptureUtilReferenceRenderError extends ScriptureUtilError {
    constructor(message) {
        super(`Failed to render verse reference: ${message}`);
        // See https://github.com/supermacro/neverthrow/issues/451
        this.scriptureUtilReferenceRenderError = scriptureUtilReferenceRenderErrorSymbol;
    }
}
/**
 * Used when the user input is invalid.
 */
class ScriptureUtilUserInputError extends ScriptureUtilError {
    constructor() {
        super(...arguments);
        // See https://github.com/supermacro/neverthrow/issues/451
        this.scriptureUtilUserInputError = scriptureUtilUserInputErrorSymbol;
    }
}
/**
 * Used when trying to perform an operation on a VID Range that is missing a translation.
 */
class ScriptureUtilMissingTranslationError extends ScriptureUtilError {
    constructor() {
        super(...arguments);
        // See https://github.com/supermacro/neverthrow/issues/451
        this.scriptureUtilMissingTranslationError = scriptureUtilMissingTranslationErrorSymbol;
    }
}
/**
 * Used when trying to perform an operation on a VID Range that refers to non-existent verses.
 */
class ScriptureUtilMissingVerseError extends ScriptureUtilError {
    constructor() {
        super(...arguments);
        // See https://github.com/supermacro/neverthrow/issues/451
        this.scriptureUtilMissingVerseError = scriptureUtilMissingVerseErrorSymbol;
    }
}
/**
 * Used when trying to perform an operation on a VID Range that is invalid, e.g.:
 * - Failed to find the first or last verse of a range.
 * - Tried to use a range across book boundaries, where it isn't supported.
 */
class ScriptureUtilInvalidVidRangeError extends ScriptureUtilError {
    constructor() {
        super(...arguments);
        // See https://github.com/supermacro/neverthrow/issues/451
        this.scriptureUtilInvalidVidRangeError = scriptureUtilInvalidVidRangeErrorSymbol;
    }
}

class VidRange {
    /**
     * Represents a range of verses.
     *
     * @param fromVid
     *   The first verse ID in the range.
     * @param toVid
     *   The last verse ID in the range.
     * @param tid
     *   The translation ID. Optional.
     * @param wordOrdinals
     *   A comma-separated list or array of integers representing the word ordinals referred to by this VID range.
     *   This only makes sense when fromVid == toVid AND tid is set.
     */
    constructor(fromVid, toVid, tid = null, wordOrdinals = null) {
        // Automatically fix inverted ranges.
        if (toVid < fromVid) {
            this.fromVid = toVid;
            this.toVid = fromVid;
        }
        else {
            this.fromVid = fromVid;
            this.toVid = toVid;
        }
        this.tid = tid;
        this.wordOrdinals = parseWordOrdinals(wordOrdinals);
    }
    /**
     * Check if this verse range is exactly the same as another.
     */
    sameAs(another) {
        if (!another) {
            return false;
        }
        return (this.fromVid === another.fromVid &&
            this.toVid === another.toVid &&
            this.tid === another.tid &&
            this.wordOrdinals.join(",") ===
                parseWordOrdinals(another.wordOrdinals || null).join(","));
    }
    /**
     * Make a deep copy of this VID Range.
     */
    clone() {
        return new VidRange(this.fromVid, this.toVid, this.tid, this.wordOrdinals.join(","));
    }
    /**
     * Check if this VID Range refers to a single verse or not.
     *
     * @returns
     *   True if fromVid and toVid are the same, i.e., a single verse.
     */
    get single() {
        return this.fromVid === this.toVid;
    }
    /**
     * Convert the VID range to a string, ignoring the translation ID and word ordinals.
     */
    get vidsString() {
        if (this.single) {
            return `${this.fromVid}`;
        }
        else {
            return `${this.fromVid}-${this.toVid}`;
        }
    }
    /**
     * Check if this VID range overlaps with the given object.
     *
     * @param target
     *   The object to check. It can be another VID range, or any object with
     *   `fromVid` and `toVid` properties, like `Verse`.
     *
     * @returns
     *   True if there is some overlap.
     */
    overlapsWith(target) {
        return (Math.max(this.fromVid, target.fromVid) <=
            Math.min(this.toVid, target.toVid));
    }
    /**
     * Check if the given range is FULLY contained inside this range.
     */
    contains(range) {
        return range.fromVid >= this.fromVid && range.toVid <= this.toVid;
    }
    /**
     * Check if this range is FULLY contained inside the given range.
     */
    containedBy(range) {
        return this.fromVid >= range.fromVid && this.toVid <= range.toVid;
    }
    /**
     * Dump data in a format understood by the back end.
     *
     * Inverse of `createVidRange`.
     */
    dump() {
        return {
            fromVid: this.fromVid,
            toVid: this.toVid,
            tid: this.tid,
            wordOrdinals: this.wordOrdinals.join(","),
        };
    }
    /**
     * Return a new VID range, using the provided translation.
     * Everything else stays the same. The new object is a deep clone.
     */
    changeTranslation(tid) {
        return new VidRange(this.fromVid, this.toVid, tid, this.wordOrdinals.join(","));
    }
    /**
     * Return a new VID range, using the provided word ordinals.
     * Everything else stays the same. The new object is a deep clone.
     */
    changeWordOrdinals(ordinals) {
        return new VidRange(this.fromVid, this.toVid, this.tid, ordinals);
    }
    /**
     * Subtract the given range from this range.
     *
     * @param range
     *   The range to subtract from this one.
     *
     * @return
     *   An array of VID ranges.
     *   - Two ranges if the subtraction removed verses from the middle of this range.
     *   - One range if the subtraction removed verses from the beginning or end of this range.
     *   - No ranges if the subtraction removed all verses.
     */
    subtract(range) {
        if (!this.overlapsWith(range)) {
            return [this.clone()];
        }
        let front = null;
        if (this.fromVid <= range.fromVid - 1) {
            front = new VidRange(this.fromVid, range.fromVid - 1, this.tid);
        }
        let back = null;
        if (range.toVid + 1 <= this.toVid) {
            back = new VidRange(range.toVid + 1, this.toVid, this.tid);
        }
        if (front && back) {
            // The VID range was split in two, because verses were removed in the middle.
            return [front, back];
        }
        if (front) {
            // Verses were removed from the end.
            return [front];
        }
        if (back) {
            // Verses were removed from the front.
            return [back];
        }
        // All verses were removed.
        return [];
    }
    /**
     * Return a new range that includes this range and the given range.
     */
    expandToInclude(range) {
        return new VidRange(Math.min(this.fromVid, range.fromVid), Math.max(this.toVid, range.toVid), this.tid);
    }
    /**
     * Render a reference to this verse range.
     *
     * Use an array of Verse objects to determine book names, verse labels, etc. instead of querying the scripture server.
     *
     * @param verses
     *   The verses for which to format the range.
     *   This array must contain all verses referred to by this VID range.
     * @param showTranslation
     *   Whether to include the translation in the rendered string. If `tid` is not set, this is ignored.
     * @param plain
     *   Whether to omit all HTML tags in output
     *
     * @returns
     *   The rendered, human-readable verse range, or null if it's not possible to render it.
     */
    renderReferenceUsingVerses(verses, showTranslation, plain = false) {
        // We need the first and last verse in our range:
        const from = verses.find(this.overlapsWith, this);
        const to = findLast(verses, this.overlapsWith, this);
        if (!from || !to) {
            // Not possible to render the verse range if we don't have the first and last verse.
            return null;
        }
        return formatVerseRange(from.label, to.label, from.bookName, `${from.chapter}`, `${to.chapter}`, showTranslation && this.tid
            ? plain
                ? ` (${from.translationAbbr})`
                : ` (<abbr title="${from.translationName}">${from.translationAbbr}</abbr>)`
            : "");
    }
    /**
     * Render a reference for a verse range.
     *
     * Use the scripture client to determine book names, verse labels, etc.
     *
     * @param client
     *   The scripture client used to load info from the server.
     * @param localTid
     *   The ID of a translation in the local app language, used to display book names in a more understandable format.
     *   This is useful for users when the range being formatted is in a different language than the user interface.
     * @param showTranslation
     *   Whether to include the translation in the rendered string.
     * @param plain
     *   Whether to omit all HTML tags in output
     * @param partLabel
     *   An optional verse part label, like "a" in "1:1a".
     *   - True: Include a verse part label based on the word ordinals.
     *   - False: Omit the verse part label.
     *   - A string: Use this string as the verse part label.
     *
     * @returns
     *   The rendered, human-readable verse range.
     */
    renderReference(client, localTid, showTranslation, plain, partLabel = false) {
        const tid = this.tid || localTid;
        const range = this.changeTranslation(tid);
        const translationResult = client
            .getTranslation(tid)
            .mapErr((e) => e instanceof ScriptureHttpError && e.response.status == 404
            ? new ScriptureUtilReferenceRenderError(`Translation ${tid} not found.`)
            : e);
        const boundaryVersesResult = ResultAsync.combine([
            range.getFirstVerse(client),
            range.getLastVerse(client),
        ]).andThen(([firstVerse, lastVerse]) => {
            if (!firstVerse || !lastVerse) {
                return err(new ScriptureUtilReferenceRenderError(`Boundary verses ${range.fromVid} and ${range.toVid} not found in translation ${tid}.`));
            }
            if (firstVerse.bid !== lastVerse.bid) {
                return err(new ScriptureUtilReferenceRenderError(`Boundary verses ${firstVerse.fromVid} and ${lastVerse.toVid} are not in the same book.`));
            }
            return ok({ firstVerse, lastVerse });
        });
        const booksResult = boundaryVersesResult.andThen((boundaryVerses) => {
            const bid = boundaryVerses.firstVerse.bid;
            const bookResult = client
                .getBook(tid, bid)
                .mapErr((e) => e instanceof ScriptureHttpError && e.response.status == 404
                ? new ScriptureUtilReferenceRenderError(`Book ${bid} not found in translation ${tid}.`)
                : e);
            const localBookResult = localTid == tid
                ? bookResult
                : client.getBook(localTid, bid).orElse((e) => {
                    if (e instanceof ScriptureHttpError && e.response.status == 404) {
                        return bookResult;
                    }
                    else {
                        return err(e);
                    }
                });
            return ResultAsync.combine([
                bookResult,
                localBookResult,
            ]).map(([book, localBook]) => ({ book, localBook }));
        });
        return ResultAsync.combine([
            translationResult,
            boundaryVersesResult,
            booksResult,
        ]).map(([translation, boundaryVerses, books]) => {
            var _a;
            const { firstVerse, lastVerse } = boundaryVerses;
            const { book, localBook } = books;
            const translationStr = getTranslationSuffix(showTranslation, translation.name, translation.abbreviation, plain);
            let partLabelStr;
            if (!this.single) {
                partLabelStr = "";
            }
            else if (typeof partLabel === "string") {
                partLabelStr = partLabel;
            }
            else if (partLabel &&
                this.wordOrdinals.length &&
                firstVerse.parts.length) {
                const part = findVersePartContainingAllOrdinals(firstVerse.parts.map(createPartialVerse), this.wordOrdinals);
                partLabelStr = (_a = part === null || part === void 0 ? void 0 : part.label) !== null && _a !== void 0 ? _a : "";
            }
            else {
                partLabelStr = "";
            }
            return formatVerseRange(firstVerse.label, lastVerse.label, formatBookName(book, localBook), firstVerse.chapter.toString(), lastVerse.chapter.toString(), `${partLabelStr}${translationStr}`);
        });
    }
    getFirstVerse(client) {
        if (!this.tid) {
            return errAsync(new ScriptureUtilMissingTranslationError("Can't get verses from a range without a translation ID"));
        }
        if (this.single) {
            // For a single verse, the exact ID must exist in the translation.
            const verseResult = client.getVerseById(this.tid, this.fromVid);
            return verseResult.orElse((e) => {
                if (e instanceof ScriptureHttpError && e.response.status == 404) {
                    return okAsync(null);
                }
                else {
                    return errAsync(e);
                }
            });
        }
        // For a range, sometimes the boundary VID does not exist in the translation, so we use the next best one inside
        // the required range.
        return client
            .getVersesStartingAtId(this.tid, this.fromVid, 1)
            .map((vv) => vv[0] || null);
    }
    getLastVerse(client) {
        if (!this.tid) {
            return errAsync(new ScriptureUtilMissingTranslationError("Can't get verses from a range without a translation ID"));
        }
        if (this.single) {
            return this.getFirstVerse(client);
        }
        // For a range, sometimes the boundary VID does not exist in the translation, so we use the next best one inside
        // the required range.
        return client
            .getVersesEndingAtId(this.tid, this.toVid, 1)
            .map((vv) => vv[0] || null);
    }
    getAllVerses(client) {
        if (!this.tid) {
            return errAsync(new ScriptureUtilMissingTranslationError("Can't get verses from a range without a translation ID"));
        }
        return client.getVersesByIdRange(this.tid, this.fromVid, this.toVid);
    }
    /**
     * Helper method for `addVersesAfter`, `removeVersesBefore` etc.
     *
     * Only ranges within a book are allowed.
     *
     * @param client The scripture client used to get info from the scripture server.
     *
     * @returns Useful info required when making decisions about adding to or removing from this range.
     */
    addRemoveHelper(client) {
        const tid = this.tid;
        if (!tid) {
            return errAsync(new ScriptureUtilMissingTranslationError(`Can't add or remove verses from a range without a translation: ${this.fromVid}-${this.toVid}`));
        }
        const boundaryVersesResult = ResultAsync.combine([
            this.getFirstVerse(client),
            this.getLastVerse(client),
        ]).andThen(([firstVerse, lastVerse]) => {
            if (!firstVerse || !lastVerse) {
                return errAsync(new ScriptureUtilInvalidVidRangeError(`Failed to find bounding verses of range ${tid}/${this.fromVid}-${this.toVid}`));
            }
            if (firstVerse.bid !== lastVerse.bid) {
                return errAsync(new ScriptureUtilInvalidVidRangeError(`Ranges across book boundaries are not supported: ${tid}/${this.fromVid}-${this.toVid}`));
            }
            return okAsync({ firstVerse, lastVerse });
        });
        const vidsInBookResult = boundaryVersesResult.andThen(({ firstVerse }) => client.getVerseIds(tid, firstVerse.bid, null));
        return ResultAsync.combine([
            boundaryVersesResult,
            vidsInBookResult,
        ]).andThen(([boundaryVerses, vidsInBook]) => {
            const vidTouchesOurRange = (vid) => this.fromVid <= vid && this.toVid >= vid;
            const firstVerseIndex = vidsInBook.findIndex(vidTouchesOurRange);
            const lastVerseIndex = findLastIndex(vidsInBook, vidTouchesOurRange);
            if (firstVerseIndex < 0 || lastVerseIndex < 0) {
                return err(new ScriptureUtilInvalidVidRangeError(`Failed to find bounding verses of range ${tid}/${this.fromVid}-${this.toVid}`));
            }
            const numVerses = lastVerseIndex - firstVerseIndex + 1;
            return ok({
                firstVerse: boundaryVerses.firstVerse,
                lastVerse: boundaryVerses.lastVerse,
                vidsInBook,
                firstVerseIndex,
                lastVerseIndex,
                numVerses,
            });
        });
    }
    /**
     * Return a new VID range, expanded by n verses at the end, if available in the given translation.
     * Crossing book boundaries is not allowed.
     *
     * @param client
     *   The scripture client used to get info from the scripture server.
     * @param n
     *   The number of verses to add at the end.
     * @param max
     *   The maximum number of verses. If the expanded range would contain more verses than this, some verses are removed
     *   from the front to compensate for that.
     */
    addVersesAfter(client, n, max) {
        return this.addRemoveHelper(client).map((metadata) => {
            // If `over` is greater than 0, then adding more verses would exceed the maximum number of verses to display. We
            // prevent that by removing some verses from the start.
            const over = metadata.numVerses + n - max;
            const newFirstVerseIndex = over > 0
                ? Math.min(metadata.firstVerseIndex + over, metadata.vidsInBook.length - max)
                : metadata.firstVerseIndex;
            const newFirstVerseFromVid = metadata.vidsInBook[newFirstVerseIndex];
            // The last verse is always incremented by the desired amount, except when fewer verses are available, in which
            // case we simply extend it to the last verse.
            const newLastVerseIndex = Math.min(metadata.lastVerseIndex + n, metadata.vidsInBook.length - 1);
            // We could do an extra back-end query to get the toVid of the last verse in the range, to make sure it includes
            // the entire compound verse. But this is not necessary because a range that touches a compound verse will already
            // render as if it includes the entire compound verse.
            const newLastVerseFromVid = metadata.vidsInBook[newLastVerseIndex];
            if (!newFirstVerseFromVid || !newLastVerseFromVid) {
                // Failed to find the new first / last verse. Return the same range.
                return this.clone();
            }
            return new VidRange(newFirstVerseFromVid, newLastVerseFromVid, this.tid, this.wordOrdinals);
        });
    }
    /**
     * Return a new VID range, expanded by n verses at the start, if available in the given translation.
     * Crossing book boundaries is not allowed.
     *
     * @param client
     *   The scripture client used to get info from the scripture server.
     * @param n
     *   The number of verses to add at the end.
     * @param max
     *   The maximum number of verses. If the expanded range would contain more verses than this, some verses are removed
     *   from the front to compensate for that.
     */
    addVersesBefore(client, n, max) {
        return this.addRemoveHelper(client).map((metadata) => {
            // If `over` is greater than 0, then adding more verses would exceed the maximum number of verses to display. We
            // prevent that by removing some verses from the end.
            const over = metadata.numVerses + n - max;
            const newLastVerseIndex = over > 0
                ? Math.max(metadata.lastVerseIndex - over, max - 1)
                : metadata.lastVerseIndex;
            // We could do an extra back-end query to get the toVid of the last verse in the range, to make sure it includes
            // the entire compound verse. But this is not necessary because a range that touches a compound verse will already
            // render as if it includes the entire compound verse.
            const newLastVerseFromVid = metadata.vidsInBook[newLastVerseIndex];
            // The first verse is always decremented by the desired amount, except when fewer verses are available, in which
            // case we simply extend it to the first verse.
            const newFirstVerseIndex = Math.max(metadata.firstVerseIndex - n, 0);
            const newFirstVerseFromVid = metadata.vidsInBook[newFirstVerseIndex];
            if (!newFirstVerseFromVid || !newLastVerseFromVid) {
                // Failed to find the new first / last verse. Return the same range.
                return this.clone();
            }
            return new VidRange(newFirstVerseFromVid, newLastVerseFromVid, this.tid, this.wordOrdinals);
        });
    }
    /**
     * Return a new VID range, shrunk by n verses at the end.
     *
     * @param client
     *   The scripture client used to get info from the scripture server.
     * @param n
     *   The number of verses to add at the end.
     * @param min
     *   The minimum number of verses. The range will not be shrunk down beyond this point.
     */
    removeVersesAfter(client, n, min) {
        return this.addRemoveHelper(client).andThen((metadata) => {
            if (metadata.numVerses <= min) {
                return ok(this.clone());
            }
            // Remove verses from the end without subceeding the minimum.
            const remove = Math.min(n, metadata.numVerses - min);
            const newLastVerseIndex = Math.max(metadata.lastVerseIndex - remove, 0);
            // We could do an extra back-end query to get the toVid of the last verse in the range, to make sure it includes
            // the entire compound verse. But this is not necessary because a range that touches a compound verse will
            // already render as if it includes the entire compound verse.
            const newLastVerseFromVid = metadata.vidsInBook[newLastVerseIndex];
            if (!newLastVerseFromVid) {
                return err(new ScriptureUtilInvalidVidRangeError("Failed to find the last verse in the range."));
            }
            return ok(new VidRange(this.fromVid, newLastVerseFromVid, this.tid, this.wordOrdinals));
        });
    }
    /**
     * Return a new VID range, shrunk by n verses at the start.
     *
     * @param client
     *   The scripture client used to get info from the scripture server.
     * @param n
     *   The number of verses to add at the start.
     * @param min
     *   The minimum number of verses. The range will not be shrunk down beyond this point.
     */
    removeVersesBefore(client, n, min) {
        return this.addRemoveHelper(client).andThen((metadata) => {
            if (metadata.numVerses <= min) {
                return ok(this.clone());
            }
            // Remove verses from the start without subceeding the minimum.
            const remove = Math.min(n, metadata.numVerses - min);
            const newFirstVerseIndex = Math.min(metadata.firstVerseIndex + remove, metadata.vidsInBook.length - 1);
            const newFirstVerseFromVid = metadata.vidsInBook[newFirstVerseIndex];
            if (!newFirstVerseFromVid) {
                return err(new ScriptureUtilInvalidVidRangeError("Failed to find the first verse in the range."));
            }
            return ok(new VidRange(newFirstVerseFromVid, this.toVid, this.tid, this.wordOrdinals));
        });
    }
    /**
     * Return a new VID range, shrunk from the end to n verses.
     *
     * This is equivalent to removeVersesAfter with n=infinite.
     *
     * @param client
     *   The scripture client used to get info from the scripture server.
     * @param min
     *   The number of verses to shrink to. If it's already smaller, just return the same range.
     */
    limitAfter(client, min) {
        return this.removeVersesAfter(client, Number.POSITIVE_INFINITY, min);
    }
    /**
     * Return a new VID range, shrunk from the start to n verses.
     *
     * This is equivalent to removeVersesBefore with n=infinite.
     *
     * @param client
     *   The scripture client used to get info from the scripture server.
     * @param min
     *   The number of verses to shrink to. If it's already smaller, just return the same range.
     */
    limitBefore(client, min) {
        return this.removeVersesBefore(client, Number.POSITIVE_INFINITY, min);
    }
}
/**
 * Compare two arrays of VidRange objects.
 *
 * @returns
 *   True if the array elements are not essentially the same.
 */
function someRangesDiffer(a, b) {
    if (a.length !== b.length) {
        return true;
    }
    for (let i = 0; i < a.length; i++) {
        const r1 = a[i];
        const r2 = b[i];
        if (r1 && r2 && !r1.sameAs(r2)) {
            return true;
        }
    }
    return false;
}
/**
 * Compare two values, both of which may be VidRange or null.
 *
 * @returns
 *   True if the values are not equivalent.
 */
function rangeOrNullDiffers(a, b) {
    return !((!a && !b) || (a && b && a.sameAs(b)));
}
/**
 * Parse VID ranges from a string.
 *
 * @param vids
 *   A string representing a range or ranges of VIDs.
 *   Examples:
 *   - "123"
 *   - "123-129"
 *   - "123-129,181"
 *   - "123-129,181-191"
 * @param tid
 *   The default translation ID to use when constructing VID ranges.
 *
 * @returns
 *   An array of VID ranges.
 *   Each element corresponds to a part of the input where parts are separated by commas.
 *   Empty parts are omitted.
 */
function parseVidsString(vids, tid) {
    return vids
        .split(",")
        .filter((s) => s.length)
        .map((s) => {
        const parts = s
            .split("-")
            .map((n) => parseInt(n))
            .filter((n) => Number.isInteger(n));
        if (!parts[0] || parts.length > 2) {
            return null;
        }
        if (!parts[1]) {
            return new VidRange(parts[0], parts[0], tid);
        }
        return new VidRange(parts[0], parts[1], tid);
    })
        .filter((r) => r instanceof VidRange);
}
/**
 * Parse user-provided word ordinals.
 * @param value
 *   A comma-separated list or array of integers representing the word ordinals referred to by this VID range.
 */
function parseWordOrdinals(value) {
    if (typeof value === "string") {
        if (value.length < 1) {
            return [];
        }
        else {
            // Split the string on the commas and parse the integers.
            return value.split(",").map((o) => parseInt(o));
        }
    }
    else if (value === null) {
        return [];
    }
    else {
        return value.map(ensureInt);
    }
}
/**
 * Create a VidRange object from back end data.
 */
function createVidRange(data) {
    const fromVid = data.fromVid;
    const toVid = data.toVid;
    const wordOrdinals = data.wordOrdinals;
    return new VidRange(ensureInt(fromVid), ensureInt(toVid), data.tid || null, wordOrdinals || null);
}

class ScriptureQuery {
    constructor(text = null, parsed = null, range = null, rendered = null, error = null, hint = null) {
        this.text = text;
        this.parsed = parsed;
        this.range = range;
        this.rendered = rendered;
        this.error = error;
        this.hint = hint;
    }
}
/**
 * Create a ScriptureQuery from user input for a given translation.
 *
 * This promise should never reject.
 *
 * @param client The client used to get information from the back end.
 * @param tid
 *   The ID of the translation to query. The result will have this in `.range.tid`.
 * @param localTid
 *   The ID of a translation in the user's local language. Used to make book name lookups easier for the user, e.g.
 *   when looking in a French bible, the user may still type an English book name.
 * @param text The raw user input.
 */
const interpretScriptureQuery = (client, tid, localTid, text) => __awaiter(void 0, void 0, void 0, function* () {
    if (text.length < 2) {
        return new ScriptureQuery(text, null, null, null, null, "Type something.");
    }
    const parsed = parseReference(text);
    if (!parsed) {
        return new ScriptureQuery(text, null, null, null, "Did not understand what you mean.", null);
    }
    const rangeResult = yield findVidRange(client, tid, localTid, parsed);
    if (rangeResult.isErr()) {
        return new ScriptureQuery(text, parsed, null, null, rangeResult.error.message, null);
    }
    const renderedResult = yield rangeResult.value.renderReference(client, localTid, !!tid, true);
    if (renderedResult.isErr()) {
        return new ScriptureQuery(text, parsed, rangeResult.value, null, "Failed to render the verse reference.", null);
    }
    return new ScriptureQuery(text, parsed, rangeResult.value, renderedResult.value, null, renderedResult.value);
});
/**
 * Find VID range in the translation index based on the given parsed verse reference.
 *
 * @param client The client used to get information from the back end.
 * @param tid
 *   The ID of the translation to query. The result will have this in `.range.tid`.
 * @param localTid
 *   The ID of a translation in the user's local language. Used to make book name lookups easier for the user, e.g.
 *   when looking in a French bible, the user may still type an English book name.
 * @param parsed The parsed user input.
 *
 * @returns
 *   A ResultAsync object containing the VID range (without tid), or an error. When the error is a
 *   `ScriptureUtilUserInputError`, it is meant to provide feedback to the user about their input.
 */
function findVidRange(client, tid, localTid, parsed) {
    if (!parsed.book) {
        return errAsync(new ScriptureUtilUserInputError("No book provided."));
    }
    return findBook(client, tid, localTid, parsed.book).andThen((book) => {
        return findChapterNumbers(parsed, book).asyncAndThen((chapterNumbers) => {
            const fromVerseResult = findFirstVerse(client, tid, localTid, parsed.fromVerse, chapterNumbers.fromChapter, book);
            const toVerseResult = findLastVerse(client, tid, localTid, parsed.toVerse, chapterNumbers.toChapter, book);
            return ResultAsync.combine([
                fromVerseResult,
                toVerseResult,
            ]).andThen(([fromVerse, toVerse]) => createVidRangeFromVerses(fromVerse, toVerse, tid));
        });
    });
}
function findBook(client, tid, localTid, parsedBookInput) {
    // Find books matching the name the user typed.
    const booksResult = getAllBooks(client, tid, localTid).map((b) => findBooks(b, parsedBookInput));
    // If available, use a book that starts with exactly what the user typed. Fall back to using the first book.
    // This ensures that strings like "Isa" return "Isaiah" rather than "I Samuel".
    // Do not assume that the array of books is ordered, or that the array keys correspond to the book ordinals.
    return booksResult
        .map((books) => books.find((b) => b.name.toLowerCase().startsWith(parsedBookInput.toLowerCase())) || books[0])
        .andThen((book) => book
        ? ok(book)
        : err(new ScriptureUtilUserInputError(`Book '${parsedBookInput}' not found in this translation.`)));
}
function getAllBooks(client, tid, localTid) {
    if (localTid === tid || !tid) {
        return client.getBooks(localTid);
    }
    return ResultAsync.combine([
        client.getBooks(tid),
        client.getBooks(localTid),
    ]).map(([books, localBooks]) => [...books, ...localBooks]);
}
function findChapterNumbers(userInput, book) {
    const fromChapter = userInput.fromChapter || book.fromChapter;
    const toChapter = userInput.toChapter || book.toChapter;
    if (fromChapter > book.toChapter ||
        fromChapter < book.fromChapter ||
        toChapter > book.toChapter ||
        toChapter < book.fromChapter) {
        return err(new ScriptureUtilUserInputError(`Book '${book.name}' only has chapters ${book.fromChapter}–${book.toChapter} in ${book.translationAbbr}.`));
    }
    if (fromChapter > toChapter) {
        return err(new ScriptureUtilUserInputError("Chapter numbers are inverted."));
    }
    return ok({ fromChapter, toChapter });
}
function findFirstVerse(client, tid, localTid, fromVerse, fromChapterNumber, book) {
    if (fromVerse) {
        // The user specified the starting verse.
        return client
            .getVerseByReference(tid || localTid, book.bid, fromChapterNumber, fromVerse)
            .mapErr((e) => {
            if (e instanceof ScriptureHttpError && e.response.status == 404) {
                return new ScriptureUtilUserInputError(`Did not find ${book.name} ${fromChapterNumber}:${fromVerse} in ${book.translationAbbr}.`);
            }
            return e;
        });
    }
    // Starting verse was omitted. Assume we want the first verse in the chapter.
    return client
        .getChapter(tid || localTid, book.bid, fromChapterNumber)
        .andThen((chapter) => client.getVerseById(tid || localTid, chapter.fromVid))
        .mapErr((e) => {
        if (e instanceof ScriptureHttpError && e.response.status == 404) {
            return new ScriptureUtilMissingVerseError(`Failed to find the first verse of ${book.name} ${fromChapterNumber} in ${book.translationAbbr}.`);
        }
        return e;
    });
}
function findLastVerse(client, tid, localTid, toVerse, toChapterNumber, book) {
    if (toVerse) {
        // The user specified the ending verse.
        return client
            .getVerseByReference(tid || localTid, book.bid, toChapterNumber, toVerse)
            .mapErr((e) => {
            if (e instanceof ScriptureHttpError && e.response.status == 404) {
                return new ScriptureUtilUserInputError(`Did not find ${book.name} ${toChapterNumber}:${toVerse} in ${book.translationAbbr}.`);
            }
            return e;
        });
    }
    // Ending verse was omitted. Assume we want the last verse in the chapter.
    return client
        .getChapter(tid || localTid, book.bid, toChapterNumber)
        .andThen((chapter) => client.getVerseById(tid || localTid, chapter.toVid))
        .mapErr((e) => {
        if (e instanceof ScriptureHttpError && e.response.status == 404) {
            return new ScriptureUtilMissingVerseError(`Failed to find the last verse of ${book.name} ${toChapterNumber} in ${book.translationAbbr}.`);
        }
        return e;
    });
}
function createVidRangeFromVerses(fromVerse, toVerse, tid) {
    if (toVerse.toVid < fromVerse.fromVid) {
        return err(new ScriptureUtilUserInputError("Verse range is inverted."));
    }
    return ok(new VidRange(fromVerse.fromVid, toVerse.toVid, tid));
}
/**
 * Helper function to replace roman numerals at the start of a book's name
 * with arabic numerals for easier matching with the user input.
 *
 * @param str
 *   The book name to replace the numerals in.
 *
 * @returns
 *   The book name with numerals replaced.
 */
function romanToArabicNumerals(str) {
    return str.replace(/^III/, "3").replace(/^II/, "2").replace(/^I/, "1");
}
/**
 * Get a predicate function for finding books that start with the given name or matches the abbreviation.
 *
 * @param bookName The partial name or abbreviation to search for.
 */
function getBookPredicate(bookName) {
    const cmp1 = romanToArabicNumerals(bookName).replace(/\s/g, "");
    return (book) => {
        // First check for books starting with the given name.
        const cmp2 = romanToArabicNumerals(book.name).replace(/\s/g, "");
        // Since we are using localeCompare instead of startsWith, we need to extract the characters for comparison.
        const len = Math.min(cmp1.length, cmp2.length);
        if (cmp1.localeCompare(cmp2.substring(0, len), "en", {
            usage: "search",
            sensitivity: "base",
        }) === 0) {
            return true;
        }
        if (bookName.localeCompare(book.name.substring(0, len), "en", {
            usage: "search",
            sensitivity: "base",
        }) === 0) {
            return true;
        }
        // Then check for books with matching abbreviations.
        return (book.abbreviations.findIndex((a) => a.toLowerCase() === bookName.toLowerCase()) >= 0);
    };
}
/**
 * Find books that start with the given name or matches the abbreviation.
 *
 * @param books The array of books in which to search.
 * @param bookName The partial name or abbreviation to search for.
 *
 * @returns An array of matching book IDs.
 */
function findBooks(books, bookName) {
    if (bookName.length < 2) {
        // Don't waste time searching for an empty name or just one character.
        return [];
    }
    return books.filter(getBookPredicate(bookName));
}

function getQuoteSuffix(text, plain) {
    if (!text.length) {
        return "";
    }
    return plain ? ` "${text}"` : ` <q>${text}</q>`;
}
function countNumberOfHeadingWordsToSkip(text) {
    const headingRegexResult = allHeadingsRegex.exec(text);
    if (!headingRegexResult) {
        return 0;
    }
    const matchWord = headingRegexResult[0].match(wordRegex);
    if (!matchWord) {
        return 0;
    }
    return matchWord.length;
}
/**
 * The scripture database contains some non-HTML formatting. Replace it with proper HTML tags.
 *
 * TODO: Just serve proper HTML from the scripture server to begin with!
 */
function replaceFormattingWithHtml(text) {
    // HTML emphasis.
    text = text.replace(/\[i]/g, "<em>").replace(/\[\/i]/g, "</em>");
    // Replace all occurrences of "[br]" with "<br>"
    return text.split("[br]").join("<br>");
}
class Verse {
    /**
     * Represents a Bible verse in a specific translation.
     *
     * @param fromVid
     *   The first verse ID represented by this verse.
     * @param toVid
     *   The last verse ID represented by this verse. If this is not a compound verse, `toVid` must equal `fromVid`.
     * @param tid
     *   The ID of the translation this verse belongs to.
     * @param bid
     *   The book ID.
     * @param chapter
     *   The chapter ordinal.
     * @param label
     *   The verse's label. Usually a number, but can be something like "3b".
     * @param text
     *   The content of the verse.
     * @param partialVerses
     *   An array of partial verse definitions.
     * @param bookName
     *   The name of the book this verse is from.
     * @param translationName
     *   The name of the translation this verse is from.
     * @param translationAbbr
     *   The abbreviation of the translation this verse is from.
     */
    constructor(fromVid, toVid, tid, bid, chapter, label, text, partialVerses, bookName, translationName, translationAbbr) {
        this.fromVid = fromVid;
        this.toVid = toVid;
        this.tid = tid;
        this.bid = bid;
        this.chapter = chapter;
        this.label = label;
        this.text = text;
        this.partialVerses = partialVerses || [];
        this.bookName = bookName;
        this.translationName = translationName;
        this.translationAbbr = translationAbbr;
    }
    /**
     * Render a reference to this verse.
     *
     * @param plain
     *   Whether to omit all HTML tags in output.
     * @param partLabel
     *   An optional verse part label, like "a" in "1:1a".
     * @param showTranslation
     *   Whether to show the translation info if available.
     *
     * @returns
     *   The rendered reference.
     */
    renderReference(plain = false, partLabel = "", showTranslation = true) {
        const translationStr = getTranslationSuffix(showTranslation, this.translationName, this.translationAbbr, plain);
        const chapterLabel = this.chapter.toString();
        return formatVerseRange(this.label, this.label, this.bookName || "", chapterLabel, chapterLabel, `${partLabel}${translationStr}`);
    }
    /**
     * Render a reference to this verse, and quote the given words.
     *
     * @param wordOrdinals
     *   The word ordinals to quote. Empty array to quote none.
     * @param useEntirePartial
     *   Whether to show all words from the defined partial verse that contains the given word ordinals.
     *   If true, show the entire partial verse surrounding the given word ordinals.
     *   If false, show only the words provided in `wordOrdinals`.
     *   If no word ordinals are provided, this is ignored, and no words are quoted.
     * @param plain
     *   Whether to omit all HTML tags in output.
     * @param showTranslation
     *   Whether to show the translation info if available.
     *
     * @returns
     *   The rendered reference.
     */
    renderReferenceAndQuote(wordOrdinals = [], useEntirePartial = false, plain = false, showTranslation = true) {
        const translationStr = getTranslationSuffix(showTranslation, this.translationName, this.translationAbbr, plain);
        // `bookName` may be empty.
        const bookStr = this.bookName || "";
        const chapterLabel = this.chapter.toString();
        if (!this.partialVerses.length || !useEntirePartial) {
            // There are no partial verses to use,
            // or we have been instructed to ignore them.
            const quoted = getQuoteSuffix(getWordOrdinalString(this.words, wordOrdinals), plain);
            return formatVerseRange(this.label, this.label, bookStr, chapterLabel, chapterLabel, `${translationStr}${quoted}`);
        }
        /* We have partial verses, and should try to use them. */
        const part = findVersePartContainingAllOrdinals(this.partialVerses, wordOrdinals);
        if (!part) {
            // The partial verses containing the selected words were not found, or
            // the selected words cover more than one partial verse.
            // Don't quote anything.
            return formatVerseRange(this.label, this.label, bookStr, chapterLabel, chapterLabel, translationStr);
        }
        // Selected words only covers one partial verse.
        // Quote the entire partial verse, and show the part label.
        const quoted = getQuoteSuffix(this.words.slice(part.fromOrdinal, part.toOrdinal + 1).join(" "), plain);
        const verseLabel = `${this.label}${part.label}`;
        return formatVerseRange(verseLabel, verseLabel, bookStr, chapterLabel, chapterLabel, `${translationStr}${quoted}`);
    }
    /**
     * Extract words one by one from the text (including headings).
     *
     * @returns
     */
    get words() {
        return this.text.match(wordRegex) || [];
    }
    /**
     * Extract headings like [h]The Beginning[/h] from the text.
     *
     * @returns
     *   The extracted headings.
     */
    get headings() {
        let match;
        const result = [];
        while ((match = separateHeadingsRegex.exec(this.text)) !== null) {
            if (match[1]) {
                result.push(match[1]);
            }
        }
        return result;
    }
    /**
     * Make a heading for this verse.
     *
     * @param includeTextHeadings
     *   Whether to include headings from the text, if available.
     * @param includeBookChapter
     *   Whether to include the book name and chapter number.
     * @param includeTranslation
     *   Whether to include the translation in the heading.
     *   Ignored if includeBookChapter is false.
     */
    makeHeading(includeTextHeadings, includeBookChapter, includeTranslation) {
        const headings = [];
        if (includeBookChapter) {
            if (includeTranslation) {
                headings.push(`${this.bookName} ${this.chapter} (${this.translationAbbr})`);
            }
            else {
                headings.push(`${this.bookName} ${this.chapter}`);
            }
        }
        if (includeTextHeadings) {
            headings.push(...this.headings);
        }
        return headings.join("<br>");
    }
    /**
     * Whether this verse is the first verse in the containing chapter.
     *
     * TODO: Add a "firstInChapter" property to the data model?
     */
    get firstInChapter() {
        // Assume the first verse in the chapter is "1" or "1a".
        return parseInt(this.label) === 1;
    }
    /**
     * Format the verse text as HTML to display to users.
     */
    getFormattedText(highlightWordOrdinals = []) {
        const nSkipWords = countNumberOfHeadingWordsToSkip(this.text);
        const wordsAfterHeadings = this.words.slice(nSkipWords);
        // Wrap `span`s around all defined partial verses.
        this.partialVerses.forEach((part) => {
            wrapWords(wordsAfterHeadings, part.fromOrdinal, part.toOrdinal, `<span class='partial-verse' data-label='${part.label}'>`, "</span>", -nSkipWords);
        });
        // Wrap `strong`s around all ranges of highlighted words.
        findRanges(ordinalsToBooleans(highlightWordOrdinals, this.words.length), (x) => x).forEach((highlightedRange) => {
            wrapWords(wordsAfterHeadings, highlightedRange[0], highlightedRange[1] - 1, "<strong>", "</strong>", -nSkipWords);
        });
        return replaceFormattingWithHtml(wordsAfterHeadings.join(" "));
    }
    /**
     * Format the text from one verse part as HTML to display to users.
     */
    getFormattedPartText(part, highlightWordOrdinals = []) {
        const words = this.words.slice(part.fromOrdinal, part.toOrdinal + 1);
        // Wrap `strong`s around all ranges of highlighted words.
        findRanges(ordinalsToBooleans(highlightWordOrdinals, this.words.length), (x) => x).forEach((highlightedRange) => {
            wrapWords(words, highlightedRange[0], highlightedRange[1] - 1, "<strong>", "</strong>", -part.fromOrdinal);
        });
        return replaceFormattingWithHtml(words.join(" "));
    }
    /**
     * Dump data in format understood by the back end.
     *
     * Inverse of `createVerse`.
     */
    dump() {
        return {
            tid: this.tid,
            bid: this.bid,
            chapter: this.chapter,
            label: this.label,
            fromVid: this.fromVid,
            toVid: this.toVid,
            text: this.text,
            parts: this.partialVerses.map((part) => part.dump()),
            bookName: this.bookName,
            translationName: this.translationName,
            translationAbbr: this.translationAbbr,
        };
    }
    get range() {
        return new VidRange(this.fromVid, this.toVid, this.tid);
    }
}
/**
 * Create a Verse object from back end data.
 *
 * @param data
 *   The data received from the back end for a single verse.
 * @returns
 *   The new verse object.
 */
function createVerse(data) {
    return new Verse(data.fromVid, data.toVid, data.tid, data.bid, data.chapter, data.label, data.text, data.parts.map(createPartialVerse), data.bookName, data.translationName, data.translationAbbr);
}

/**
 * A generator version of Array.prototype.filter, with configurable maximum length.
 *
 * @param iterable The thing to loop over.
 * @param predicate The function that decides which elements to yield.
 * @param maxLength The maximum number of elements to yield.
 */
function* filter(iterable, predicate, maxLength = -1) {
    let count = 0;
    for (const x of iterable) {
        if (predicate(x)) {
            if (maxLength > -1 && count >= maxLength) {
                return;
            }
            yield x;
            count++;
        }
    }
}

/**
 * @file
 * Utilities for managing data about translations, books, chapters and verses.
 */
/**
 * Check whether the user input matches a verse label.
 *
 * @param label
 *   The real verse label.
 * @param query
 *   The user input.
 *
 * @returns
 *   True if the user input matches or falls within the domain represented by the verse label.
 */
function verseLabelMatches(label, query) {
    // Make sure we use the same type of dashes to indicate ranges.
    label = replaceDashes(label, "-");
    query = replaceDashes(query, "-");
    // The trivial case:
    if (label === query) {
        return true;
    }
    // Check if we can split and interpret the verse label.
    // This allows us to match something like "12-15" when the user asked for "13".
    const parts = label.split("-");
    if (!parts[0] || !parts[1]) {
        // Not a compound verse.
        return false;
    }
    // This is a compound verse. It's a match if the verse label is a number that falls within the range covered
    // by the compound verse.
    const from = parseInt(parts[0]);
    const to = parseInt(parts[1]);
    const num = parseInt(query);
    return num >= from && num <= to;
}
/**
 * Get an array of all Verse IDs in canonical order that exist in any of the given translations, within the given book.
 *
 * This is useful for determining the rows of the grid when showing parallel translations.
 *
 * @param client The client used to get information from the back end.
 * @param tids The IDs of the translations in which to look for verse IDs.
 * @param bid The book ID in which to look for verse IDs.
 *
 * @returns An array of Verse IDs, in ascending order, with no duplicates.
 */
function getAllVidsFromParallelTranslations(client, tids, bid) {
    return ResultAsync.combine(tids.map((tid) => client.getVerseIds(tid, bid, null))).map((vidsPerTid) => vidsPerTid
        .flat()
        .filter(distinct)
        .sort((a, b) => a - b));
}
function getBooksInForeignAndLocalTranslation(client, foreignTid, localTid) {
    const foreignBooksResult = client.getBooks(foreignTid);
    const localBooksResult = localTid === foreignTid ? foreignBooksResult : client.getBooks(localTid);
    const booksResult = ResultAsync.combine([
        foreignBooksResult,
        localBooksResult,
    ]).map(([foreignBooks, localBooks]) => zip(foreignBooks, localBooks));
    return booksResult.map((books) => books.map(([book, localBook]) => ({
        book,
        localBook,
        name: formatBookName(book, localBook),
    })));
}

const otBids = [
    "genesis",
    "exodus",
    "leviticus",
    "numbers",
    "deuteronomy",
    "joshua",
    "judges",
    "ruth",
    "1samuel",
    "2samuel",
    "1kings",
    "2kings",
    "1chronicles",
    "2chronicles",
    "ezra",
    "nehemiah",
    "esther",
    "job",
    "psalm",
    "proverbs",
    "ecclesiastes",
    "song",
    "isaiah",
    "jeremiah",
    "lamentations",
    "ezekiel",
    "daniel",
    "hosea",
    "joel",
    "amos",
    "obadiah",
    "jonah",
    "micah",
    "nahum",
    "habakkuk",
    "zephaniah",
    "haggai",
    "zechariah",
    "malachi",
];
const ntBids = [
    "matthew",
    "mark",
    "luke",
    "john",
    "acts",
    "romans",
    "1corinthians",
    "2corinthians",
    "galatians",
    "ephesians",
    "philippians",
    "colossians",
    "1thessalonians",
    "2thessalonians",
    "1timothy",
    "2timothy",
    "titus",
    "philemon",
    "hebrews",
    "james",
    "1peter",
    "2peter",
    "1john",
    "2john",
    "3john",
    "jude",
    "revelation",
];
const allBids = [...otBids, ...ntBids];

class ScriptureClientMock {
    constructor(data) {
        // These are higher-level functions, that only call methods on `this`, so we don't need to re-implement them. Since
        // JS binds 'this' to the caller, not to the class where the function is defined, we can simply assign the functions
        // here:
        this.findBookContainingVerse = ScriptureClient.prototype.findBookContainingVerse;
        this.findChapterContainingVerse = ScriptureClient.prototype.findChapterContainingVerse;
        this.findBookAndChapterContainingVerse = ScriptureClient.prototype.findBookAndChapterContainingVerse;
        this.data = data;
    }
    getTranslationTree(tid) {
        const data = this.data[tid];
        if (!data) {
            return err(createFake404Error());
        }
        return ok(new TranslationTree(data));
    }
    getBookTree(tid, bid) {
        return this.getTranslationTree(tid).andThen((t) => {
            const book = t.getBookById(bid);
            if (!book) {
                return err(createFake404Error());
            }
            return ok(book);
        });
    }
    getChapterTree(tid, bid, number) {
        return this.getBookTree(tid, bid).andThen((t) => {
            const chapter = t.getChapter(number);
            if (!chapter) {
                return err(createFake404Error());
            }
            return ok(chapter);
        });
    }
    getVerseTree(tid, vid) {
        return this.getTranslationTree(tid).andThen((t) => {
            const verse = t.getVerseById(vid);
            if (!verse) {
                return err(createFake404Error());
            }
            return ok(verse);
        });
    }
    getVerseTreeByReference(tid, bid, chapter, label) {
        return this.getChapterTree(tid, bid, chapter).andThen((t) => {
            const verse = t.data.verses.find((v) => v.label == label);
            if (!verse) {
                return err(createFake404Error());
            }
            return ok(new VerseTree(verse, t));
        });
    }
    getAllTranslations() {
        return okAsync(Object.values(this.data).reduce((previous, current) => {
            if (current) {
                previous[current.tid] = new TranslationTree(current).dump();
            }
            return previous;
        }, {}));
    }
    getTranslationIds() {
        return okAsync(Object.keys(this.data));
    }
    getTranslation(tid) {
        return this.getTranslationTree(tid)
            .map((t) => t.dump())
            .asyncAndThen((value) => okAsync(value));
    }
    getBooks(tid) {
        return this.getTranslationTree(tid)
            .map((t) => Array.from(t.iterateBooks(), (b) => b.dump()))
            .asyncAndThen((value) => okAsync(value));
    }
    getBook(tid, bid) {
        return this.getBookTree(tid, bid)
            .map((b) => b.dump())
            .asyncAndThen((value) => okAsync(value));
    }
    getChapters(tid, bid) {
        return this.getBookTree(tid, bid)
            .map((b) => Array.from(b.iterateChapters(), (c) => c.dump()))
            .asyncAndThen((value) => okAsync(value));
    }
    getChapter(tid, bid, chapter) {
        return this.getChapterTree(tid, bid, chapter)
            .map((t) => t.dump())
            .asyncAndThen((value) => okAsync(value));
    }
    getVerseIds(tid, bid, chapter) {
        if (chapter) {
            return this.getChapterTree(tid, bid, chapter)
                .map((t) => t.verseIds)
                .asyncAndThen((value) => okAsync(value));
        }
        else {
            return this.getBookTree(tid, bid)
                .map((t) => t.verseIds)
                .asyncAndThen((value) => okAsync(value));
        }
    }
    getVerseLabels(tid, bid, chapter) {
        return this.getChapterTree(tid, bid, chapter)
            .map((t) => t.verseLabels)
            .asyncAndThen((value) => okAsync(value));
    }
    getVerseIdsAndLabels(tid, bid, chapter) {
        return this.getChapterTree(tid, bid, chapter)
            .map((t) => t.verseIdsAndLabels)
            .asyncAndThen((value) => okAsync(value));
    }
    getVerseById(tid, vid) {
        return this.getVerseTree(tid, vid)
            .map((t) => t.dump())
            .asyncAndThen((value) => okAsync(value));
    }
    getVerseByReference(tid, bid, chapter, verseLabel) {
        return this.getVerseTreeByReference(tid, bid, chapter, verseLabel)
            .map((t) => t.dump())
            .asyncAndThen((value) => okAsync(value));
    }
    getVersesByIdRange(tid, fromVid, toVid) {
        return this.getTranslationTree(tid)
            .map((t) => t.getVersesById(fromVid, toVid).map((v) => v.dump()))
            .asyncAndThen((value) => okAsync(value));
    }
    getVersesStartingAtId(tid, fromVid, limit) {
        return this.getTranslationTree(tid)
            .map((t) => t.getVersesStartingAtId(fromVid, limit).map((v) => v.dump()))
            .asyncAndThen((value) => okAsync(value));
    }
    getVersesEndingAtId(tid, toVid, limit) {
        return this.getTranslationTree(tid)
            .map((t) => t.getVersesEndingAtId(toVid, limit).map((v) => v.dump()))
            .asyncAndThen((value) => okAsync(value));
    }
    search(q) {
        let matchCount = 0;
        const resultsByTranslation = {};
        const searchWords = new Set(q.text.match(wordRegex) || []);
        for (const tid of Object.keys(this.data)) {
            if (q.tids && q.tids.size && !q.tids.has(tid)) {
                // Skip this translation.
                continue;
            }
            const translationData = this.data[tid];
            if (!translationData) {
                // Skip this translation.
                continue;
            }
            const translation = new TranslationTree(translationData);
            const resultsInThisTranslation = [];
            for (const book of translation.iterateBooks()) {
                if (q.bids && q.bids.size && !q.bids.has(book.bid)) {
                    // Skip this book.
                    continue;
                }
                for (const verse of book.iterateVerses()) {
                    const verseWords = new Set(verse.text.match(wordRegex) || []);
                    let commonWords = intersection(verseWords, searchWords);
                    if (commonWords.size) {
                        resultsInThisTranslation.push({
                            verse: verse.dump(),
                            match: {
                                score: commonWords.size,
                                positions: [
                                // TODO: Only if we really need positions in unit tests.
                                ],
                            },
                        });
                    }
                }
            }
            if (resultsInThisTranslation.length) {
                resultsByTranslation[tid] = resultsInThisTranslation;
                matchCount += resultsInThisTranslation.length;
            }
        }
        return okAsync({
            matchCount,
            resultsByTranslation,
            // TODO: Implement actual pagination here?
            page: q.page || 1,
            size: q.size || matchCount,
            pageCount: Math.ceil(matchCount / (q.size || matchCount)) || 1,
        });
    }
}
/**
 * Get the intersection of two sets.
 *
 * Based on https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Set
 */
function intersection(a, b) {
    const result = new Set();
    for (const elem of b) {
        if (a.has(elem)) {
            result.add(elem);
        }
    }
    return result;
}
function createFakeResponse(body, status, statusText, url) {
    return {
        text: () => Promise.resolve(body),
        json: () => Promise.resolve(JSON.parse(body)),
        ok: status >= 200 && status < 300,
        status,
        statusText,
        url,
    };
}
function createFake404Error() {
    const text = "Not Found";
    const response = createFakeResponse(text, 404, text, "https://example.com");
    return new ScriptureHttpError(text, response, text);
}

export { ParsedReference, PartialVerse, ScriptureClientMock, ScriptureQuery, ScriptureUtilError, ScriptureUtilInvalidVidRangeError, ScriptureUtilMissingTranslationError, ScriptureUtilMissingVerseError, ScriptureUtilReferenceRenderError, ScriptureUtilUserInputError, Verse, VidRange, allBids, allHeadingsRegex, booleansToOrdinals, createPartialVerse, createVerse, createVidRange, distinct, ensureInt, filter, findBooks, findLast, findLastIndex, findRanges, findVersePartContainingAllOrdinals, findVersePartContainingOrdinal, findVidRange, formatBookName, formatVerseRange, getAllVidsFromParallelTranslations, getBookPredicate, getBooksInForeignAndLocalTranslation, getTranslationSuffix, getWordOrdinalString, interpretScriptureQuery, ntBids, ordinalsToBooleans, otBids, parseReference, parseVidsString, parseWordOrdinals, range, rangeOrNullDiffers, replaceDashes, rtlRegex, separateHeadingsRegex, someRangesDiffer, verseLabelMatches, wordRegex, wrapWords, zip };
